const USER_REJECTED_CODES = new Set([4001, "4001", "ACTION_REJECTED", "USER_REJECTED"]);
const USER_REJECTED_NAMES = new Set(["UserRejectedRequestError"]);

function collectErrorNodes(error: unknown, seen = new WeakSet<object>()): unknown[] {
  if (!error || typeof error !== "object") {
    return [error];
  }

  if (seen.has(error)) {
    return [];
  }
  seen.add(error);

  const record = error as Record<string, unknown>;
  const nodes: unknown[] = [error];
  const nestedKeys = ["cause", "error", "data", "info", "originalError"];

  for (const key of nestedKeys) {
    if (key in record) {
      nodes.push(...collectErrorNodes(record[key], seen));
    }
  }

  return nodes;
}

export function getWalletErrorMessages(error: unknown): string[] {
  const messages = new Set<string>();

  for (const node of collectErrorNodes(error)) {
    if (typeof node === "string") {
      messages.add(node);
      continue;
    }

    if (!node || typeof node !== "object") {
      continue;
    }

    const record = node as Record<string, unknown>;
    const messageKeys = ["message", "shortMessage", "details", "reason"];
    for (const key of messageKeys) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) {
        messages.add(value);
      }
    }
  }

  return [...messages];
}

export function isWalletCancellationMessage(
  message: string,
  method?: string
): boolean {
  const lower = message.toLowerCase();

  if (
    lower.includes("user rejected") ||
    lower.includes("user denied") ||
    lower.includes("user canceled") ||
    lower.includes("user cancelled") ||
    lower.includes("rejected the request") ||
    lower.includes("transaction rejected") ||
    lower.includes("request rejected") ||
    lower.includes("ethers-user-denied")
  ) {
    return true;
  }

  const hasGenericSendTransactionError = lower.includes("eth_sendtransaction error");
  if (!hasGenericSendTransactionError) {
    return false;
  }

  if (method === "eth_sendTransaction") {
    return true;
  }

  return (
    lower.includes("an unknown rpc error occurred") ||
    lower.includes("unknown rpc error") ||
    lower.includes("unknownrpcerror") ||
    lower.includes("could not coalesce error")
  );
}

export function isWalletUserRejectedError(
  error: unknown,
  method?: string
): boolean {
  const nodes = collectErrorNodes(error);

  for (const node of nodes) {
    if (!node || typeof node !== "object") {
      continue;
    }

    const record = node as Record<string, unknown>;
    if (USER_REJECTED_CODES.has(record.code as string | number)) {
      return true;
    }

    if (
      typeof record.name === "string" &&
      USER_REJECTED_NAMES.has(record.name)
    ) {
      return true;
    }
  }

  const messages = getWalletErrorMessages(error).map((message) =>
    message.toLowerCase()
  );

  if (messages.some((message) => isWalletCancellationMessage(message, method))) {
    return true;
  }

  return false;
}

export function getWalletFriendlyErrorMessage(
  error: unknown,
  method?: string
): string | null {
  if (isWalletUserRejectedError(error, method)) {
    return "User canceled";
  }

  if (
    typeof error === "string" &&
    isWalletCancellationMessage(error, method)
  ) {
    return "User canceled";
  }

  return null;
}

export function createUserRejectedRequestError(error?: unknown) {
  const normalized = new Error("User rejected the request.") as Error & {
    code: number;
    cause?: unknown;
    name: string;
  };

  normalized.code = 4001;
  normalized.name = "UserRejectedRequestError";
  if (error !== undefined) {
    normalized.cause = error;
  }

  return normalized;
}
